import { EnhancedBaseCommand } from "@crunchdao/shared-cli";
import { CrunchServiceWithContext, CrunchAccountServiceWithContext } from "@crunchdao/sdk";

export class DepositRewardCommand extends EnhancedBaseCommand {
  private getCrunchService() {
    return CrunchServiceWithContext({
      program: this.program,
    });
  }

  private getCrunchAccountService() {
    return CrunchAccountServiceWithContext({
      program: this.program,
    });
  }

  async deposit(crunchName: string, amount: string): Promise<void> {
    try {
      const crunchService = this.getCrunchService();
      const crunchAccountService = this.getCrunchAccountService();

      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);

      await this.executeOrProposeInstructions(async () => {
        const instruction = await crunchService.depositRewardUsdcInstruction(
          crunchAddress,
          parseFloat(amount),
          this.getSigner()
        );
        return [instruction];
      }, `Deposit ${amount} USDC rewards to "${crunchName}"`);

      if (!this.isMultisigMode()) {
        this.success("USDC reward deposited successfully", {
          crunchName,
          crunchAddress: crunchAddress.toBase58(),
          amount: parseFloat(amount),
        });
      }
    } catch (error) {
      this.handleDepositError(error, crunchName);
    }
  }

  private handleDepositError(error: any, crunchName?: string): never {
    // Check for missing USDC Associated Token Account error
    const isUsdcAtaError =
      error.transactionLogs &&
      error.transactionLogs.some((log: string) =>
        log.includes("Program log: Error: InvalidAccountData")
      ) &&
      error.transactionLogs.some((log: string) =>
        log.includes("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA")
      );

    if (isUsdcAtaError) {
      const coordinatorAddress = this.provider.wallet.publicKey.toBase58();
      const network = this.resolvedOptions.network || "localnet";

      // Use USDC mint addresses for different networks
      let usdcMintAddress = "";
      if (network === "mainnet") {
        usdcMintAddress = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"; // Mainnet USDC
      } else {
        usdcMintAddress = "B9yyYFe3S6i2LVrZeEpXtyVVMUr4vzrQquzzb7pLsNhy"; // Custom USDC for devnet/localnet
      }

      let createAtaInstructions = "";
      if (network === "localnet" || network === "devnet") {
        createAtaInstructions = `Run: spl-token create-account ${usdcMintAddress} --owner ${coordinatorAddress}`;
      } else if (network === "mainnet") {
        createAtaInstructions = `Create USDC Associated Token Account for ${coordinatorAddress} with mint ${usdcMintAddress}`;
      }

      const customError = this.createEnhancedError(
        `USDC Associated Token Account not found for coordinator ${coordinatorAddress}. ${createAtaInstructions}`,
        "UsdcAtaNotFoundError",
        "USDC_ATA_NOT_FOUND",
        {
          coordinatorAddress,
          network,
          createAtaInstructions,
          tokenMint: usdcMintAddress,
        },
        error
      );

      return this.handleError(customError);
    }

    // Check for insufficient USDC funds in token account
    const isInsufficientUsdcFunds =
      error.transactionLogs &&
      error.transactionLogs.some((log: string) =>
        log.includes("Program log: Error: insufficient funds")
      ) &&
      error.transactionLogs.some((log: string) =>
        log.includes("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA")
      );

    if (isInsufficientUsdcFunds) {
      const coordinatorAddress = this.provider.wallet.publicKey.toBase58();
      const network = this.resolvedOptions.network || "localnet";

      // Use USDC mint addresses for different networks
      let usdcMintAddress = "";
      if (network === "mainnet") {
        usdcMintAddress = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"; // Mainnet USDC
      } else {
        usdcMintAddress = "B9yyYFe3S6i2LVrZeEpXtyVVMUr4vzrQquzzb7pLsNhy"; // Custom USDC for devnet/localnet
      }

      let fundingInstructions = "";
      if (network === "localnet" || network === "devnet") {
        fundingInstructions = `Run: spl-token mint ${usdcMintAddress} 1000 <ATA_ADDRESS> --mint-authority <authority_keypair> --fee-payer <authority_keypair>`;
      } else if (network === "mainnet") {
        fundingInstructions = `Transfer USDC tokens to your coordinator's USDC account`;
      }

      const customError = this.createEnhancedError(
        `Insufficient USDC balance in coordinator account ${coordinatorAddress}. ${fundingInstructions}`,
        "InsufficientUsdcFundsError",
        "INSUFFICIENT_USDC_FUNDS",
        {
          coordinatorAddress,
          network,
          fundingInstructions,
          tokenMint: usdcMintAddress,
        },
        error
      );

      return this.handleError(customError);
    }

    // Fall back to generic transaction error handling
    return this.handleTransactionError(error, crunchName);
  }
}
